import IconExpandAllLines from '@apollo/icons/default/IconExpandAllLines.svg';
import IconExpandLinesDown from '@apollo/icons/default/IconExpandLinesDown.svg';
import IconExpandLinesUp from '@apollo/icons/default/IconExpandLinesUp.svg';
import classnames from 'classnames';
import React, { useMemo } from 'react';
import { createElement, createElementProps } from 'react-syntax-highlighter';

import { PlainHtmlButton } from 'src/components/common/plainHtmlButton/PlainHtmlButton';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { format } from 'src/lib/format';

import {
  DiffViewType,
  InlineTextDiff,
  LineNumberPropsFunction,
  LineType,
  RangeExpandType,
  TextDiff,
  invertRangesInDiffView,
} from '../diffViewHelpers';

type LineRendererProps = createElementProps & {
  index: number;
  lineNumberClassName?: string;
  lineProps?: LineNumberPropsFunction | ReturnType<LineNumberPropsFunction>;
  lineType: LineType;
  totalLineCount: number;
  lineNumber: number | undefined;
  type: DiffViewType;
  onMount?: () => void;
  currentGutterMarker?: {
    lineNumber: number;
    type: DiffViewType;
    marker: React.ReactNode;
  };
};

const LineRenderer = ({
  node,
  stylesheet,
  useInlineStyles,
  index,
  lineNumberClassName,
  lineProps,
  totalLineCount,
  lineType,
  lineNumber,
  type,
  onMount,
  currentGutterMarker,
}: LineRendererProps) => {
  const { className, ref, ...props } = useMemo(
    () =>
      typeof lineProps === 'function'
        ? lineProps({
            lineNumber,
            type,
            isChanged: lineType !== LineType.UNCHANGED,
            totalLineCount,
          })
        : (lineProps ?? {}),
    [lineNumber, lineProps, lineType, totalLineCount, type],
  );

  return (
    <div
      className={classnames(
        'relative flex',
        {
          'bg-error': lineType === LineType.REMOVED,
          'bg-success': lineType === LineType.ADDED,
        },
        !lineNumber && 'bg-neutral',
        className,
      )}
      ref={(el) => {
        onMount?.();
        if (typeof ref === 'function') {
          ref(el);
        }
      }}
      {...props}
    >
      {lineNumber && (
        <span
          className={classnames(
            'z-10 inline-block min-w-16 select-none pr-3 text-right text-code-a',
            // eslint-disable-next-line local/no-conditional-class-names
            lineNumberClassName,
          )}
        >
          {lineNumber}
        </span>
      )}
      <span className="ml-1 inline-block min-w-3 select-none font-mono text-primary">
        {lineType === LineType.REMOVED
          ? '-'
          : lineType === LineType.ADDED
            ? '+'
            : ' '}
      </span>
      {createElement({
        node,
        stylesheet,
        useInlineStyles,
        key: `code-segment${index}`,
      })}
      {currentGutterMarker && (
        <div className="absolute left-0 top-0 z-20">
          {currentGutterMarker.marker}
        </div>
      )}
    </div>
  );
};

export const GraphQLDiffViewLineRenderer = ({
  node,
  stylesheet,
  useInlineStyles,
  index,
  textDiff,
  lineNumberClassName,
  renderHiddenRanges,
  hiddenRanges,
  handleExpandRange,
  gutterMarkers,
  lineProps,
  type,
  onMount,
}: Omit<
  LineRendererProps,
  'lineType' | 'lineNumber' | 'currentGutterMarker' | 'totalLineCount'
> & {
  renderHiddenRanges: boolean;
  hiddenRanges?: ReturnType<typeof invertRangesInDiffView>;
  handleExpandRange?: (expandType: RangeExpandType, lineNumber: number) => void;
  textDiff: TextDiff;
  gutterMarkers?: {
    lineNumber: number;
    type: DiffViewType;
    marker: React.ReactNode;
  }[];
}) => {
  // This value is not the correct line number of the line being rendered. Because
  // some lines may not exist on either side of the diff view this number may be
  // inflated to account for those differences. This is, however, used internally by
  // our diff calculations and hidden range handling. It should not be used with
  // anything passed in by the user
  const internalLineNumber = index + 1;
  const changedLines = type === 'original' ? textDiff.removed : textDiff.added;
  const totalLineCount =
    type === 'original'
      ? textDiff.originalDiffLines.length
      : textDiff.modifiedDiffLines.length;

  // These are the line numbers that match with the original unchanged lines
  // text of either side of the diff and do not include the inflations made to match
  // up the lines of either side
  const trueLineNumbers =
    type === 'original'
      ? textDiff.originalDiffLineNumbers
      : textDiff.modifiedDiffLineNumbers;
  const lineNumber = trueLineNumbers[index];

  const matchingHiddenRange = hiddenRanges?.find(
    (range) =>
      range.startLineNumber <= internalLineNumber &&
      internalLineNumber <= range.endLineNumber,
  );

  const currentGutterMarkerIfExists = useMemo(() => {
    return gutterMarkers?.find(
      (gutterMarker) =>
        gutterMarker.lineNumber === lineNumber && gutterMarker.type === type,
    );
  }, [gutterMarkers, lineNumber, type]);

  // We don't render anything for hidden ranges and on the last line
  // we render a view with the option to expand the hidden range
  if (
    renderHiddenRanges &&
    matchingHiddenRange &&
    internalLineNumber !== matchingHiddenRange.endLineNumber
  ) {
    return null;
  } else if (
    renderHiddenRanges &&
    matchingHiddenRange &&
    internalLineNumber === matchingHiddenRange.endLineNumber
  ) {
    const hiddenRangeSize =
      matchingHiddenRange.endLineNumber -
      matchingHiddenRange.startLineNumber +
      1;
    return hiddenRangeSize > 20 ? (
      <div className="flex h-9">
        <div className="flex w-16 flex-col">
          <Tooltip label="Expand top 10 lines">
            <PlainHtmlButton
              className="focus-visible:focus-ring group flex h-1/2 w-full items-center justify-center bg-info hover:bg-blue"
              onClick={() => handleExpandRange?.('top', internalLineNumber)}
            >
              <IconExpandLinesUp className="text-icon-primary group-hover:text-inverted" />
            </PlainHtmlButton>
          </Tooltip>
          <Tooltip label="Expand bottom 10 lines">
            <PlainHtmlButton
              className="focus-visible:focus-ring group flex h-1/2 w-full items-center justify-center bg-info hover:bg-blue"
              onClick={() => handleExpandRange?.('bottom', internalLineNumber)}
            >
              <IconExpandLinesDown className="text-icon-primary group-hover:text-inverted" />
            </PlainHtmlButton>
          </Tooltip>
        </div>
        <div className="flex flex-1 items-center bg-info pl-4">
          {hiddenRangeSize} {format.pluralize(hiddenRangeSize, 'line')}
        </div>
      </div>
    ) : (
      <div className="flex h-9">
        <div className="flex w-16 flex-col">
          <Tooltip
            label={`Expand ${hiddenRangeSize} ${format.pluralize(
              hiddenRangeSize,
              'line',
            )}.`}
          >
            <PlainHtmlButton
              className="focus-visible:focus-ring group flex size-full items-center justify-center bg-info hover:bg-blue"
              onClick={() => handleExpandRange?.('full', internalLineNumber)}
            >
              {matchingHiddenRange.endLineNumber === totalLineCount ? (
                <IconExpandLinesUp className="text-icon-primary group-hover:text-inverted" />
              ) : matchingHiddenRange.startLineNumber === 1 ? (
                <IconExpandLinesDown className="text-icon-primary group-hover:text-inverted" />
              ) : (
                <IconExpandAllLines className="group-hover:text-inverted" />
              )}
            </PlainHtmlButton>
          </Tooltip>
        </div>
        <div className="flex flex-1 items-center bg-info pl-4">
          {hiddenRangeSize} {format.pluralize(hiddenRangeSize, 'line')}
        </div>
      </div>
    );
  }

  const lineType = changedLines.includes(internalLineNumber)
    ? type === 'original'
      ? LineType.REMOVED
      : LineType.ADDED
    : LineType.UNCHANGED;

  return (
    <LineRenderer
      key={index}
      node={node}
      stylesheet={stylesheet}
      useInlineStyles={useInlineStyles}
      index={index}
      lineNumberClassName={lineNumberClassName}
      lineProps={lineProps}
      lineType={lineType}
      lineNumber={lineNumber}
      totalLineCount={totalLineCount}
      type={type}
      onMount={onMount}
      currentGutterMarker={currentGutterMarkerIfExists}
    />
  );
};

export const InlineGraphQLDiffViewLineRenderer = ({
  node,
  stylesheet,
  useInlineStyles,
  textDiff,
  index,
  lineNumberClassName,
  lineProps,
  lineType,
  onMount,
}: Omit<
  LineRendererProps,
  'type' | 'lineNumber' | 'currentGutterMarker' | 'totalLineCount'
> & {
  textDiff: InlineTextDiff;
}) => {
  const lineNumber = index + 1;
  const totalLineCount = textDiff.diffLines.length;

  const originalLineNumber = textDiff.originalDiffLineNumbers[index];
  const modifiedLineNumber = textDiff.modifiedDiffLineNumbers[index];

  return (
    <div
      className={classnames('flex', {
        'bg-error': lineType === LineType.REMOVED,
        'bg-success': lineType === LineType.ADDED,
      })}
    >
      <div
        className={classnames(
          'z-10 flex select-none pr-3 text-code-a',
          // eslint-disable-next-line local/no-conditional-class-names
          lineNumberClassName,
        )}
      >
        <span className="inline-block min-w-8 text-right">
          {originalLineNumber}
        </span>
        <span className="inline-block min-w-8 text-right">
          {modifiedLineNumber}
        </span>
      </div>

      <LineRenderer
        key={index}
        node={node}
        stylesheet={stylesheet}
        useInlineStyles={useInlineStyles}
        index={index}
        // inline diff view handles line numbers a bit differently so this should always be hidden
        lineNumberClassName="hidden"
        lineProps={lineProps}
        lineNumber={lineNumber}
        totalLineCount={totalLineCount}
        // type doesn't matter for inline diff view
        type="original"
        onMount={onMount}
        lineType={lineType}
      />
    </div>
  );
};
